import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Label,
  List,
  Message,
  Popup,
  Progress,
  Segment,
} from "semantic-ui-react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import StrategyFeedback from "./StrategyFeedback";
import { buildListingDescriptionFeedbackConfig } from "./listingDescriptionFeedbackConfig";
import { convertAddressFull } from "../../../../app/common/util/util";
import {
  functionGenerateListingDescriptionStrategy,
  functionGetStrategyGenerationsForProperty,
  functionUpdateListingDescriptionFactDecision,
  functionSelectListingDescriptionVersion,
  functionUpdateListingDescriptionEdits,
} from "../../../../app/firestore/functionsService";

const WRITING_STYLES = [
  {
    value: "professional",
    label: "Professional & Feature-Forward",
    icon: "briefcase",
    description: "Crisp, polished and specific. Leads with the property's strongest features and avoids unnecessary fluff.",
  },
  {
    value: "warm",
    label: "Warm & Story-Driven",
    icon: "heart",
    description: "Inviting and descriptive. Shows how the home flows and feels while staying grounded in verified property features.",
  },
  {
    value: "editorial",
    label: "Elevated & Editorial",
    icon: "gem",
    description: "Refined, sophisticated writing emphasizing architecture, design, craftsmanship and distinctive details.",
  },
];

const LOADING_MESSAGES = [
  "Researching property details...",
  "Reviewing previous property information...",
  "Looking for community amenities...",
  "Identifying what makes this home stand out...",
  "Reviewing your draft for advertising concerns...",
  "Creating your listing descriptions...",
];

// Purely informational, client-side only (no AI/backend call) - spec section
// 12's "subtle note" for seller-motivation/transaction-message language.
// Never blocks generation, never used to infer or invent Seller motivation.
const SELLER_MOTIVATION_KEYWORDS = [
  "motivated", "concession", "closing cost", "buydown", "assumable",
  "seller financing", "quick possession", "backup offers",
];

const PRIORITY_ITEM_STATUS_META = {
  included: { icon: "check circle", color: "#2e7d32" },
  partiallyIncluded: { icon: "check circle", color: "#2e7d32" },
  rephrased_for_compliance: { icon: "exclamation triangle", color: "#b58900" },
  excluded_unverified: { icon: "exclamation triangle", color: "#b58900" },
  excluded_for_compliance: { icon: "exclamation triangle", color: "#b58900" },
  excluded_for_length: { icon: "exclamation triangle", color: "#b58900" },
};

function toDisplayDateTime(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, "MMM d, yyyy 'at' h:mm a");
}

// Same auto-suggest / freeze-on-edit pattern StrategyPricingModal.jsx uses
// for its email-body textarea: a locally-edited field only re-syncs from the
// AI/selected-version value until the agent starts typing in it themselves.
function useEditableField(sourceValue) {
  const [value, setValue] = useState(sourceValue || "");
  const [edited, setEdited] = useState(false);
  useEffect(() => {
    if (!edited) setValue(sourceValue || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceValue]);
  function onChange(newValue) {
    setValue(newValue);
    setEdited(true);
  }
  function reset() {
    setValue(sourceValue || "");
    setEdited(false);
  }
  return { value, edited, onChange, reset, setEdited };
}

export default function StrategyListingDescriptionModal({ transaction, existingListingDescriptionResult, onGenerated, onVersionSelected }) {
  const dispatch = useDispatch();

  const [step, setStep] = useState(() => (existingListingDescriptionResult ? "result" : "form"));
  const [error, setError] = useState(null);

  // Form inputs
  const [originalDraft, setOriginalDraft] = useState("");
  const [emphasis, setEmphasis] = useState("");
  const [writingStyle, setWritingStyle] = useState("professional");
  const [priorityItems, setPriorityItems] = useState([]); // [{id, text}]
  const [priorityItemDraft, setPriorityItemDraft] = useState("");
  const [otherPropertyDetails, setOtherPropertyDetails] = useState("");
  const [regenerationInstruction, setRegenerationInstruction] = useState("");

  // Result state
  const [result, setResult] = useState(existingListingDescriptionResult || null);
  // Mirrors StrategyPricingModal.jsx's pendingRequestIdRef: identifies one
  // logical generation attempt so a retry (clicking Generate/Regenerate
  // again after a failure) resumes/replays the SAME request server-side
  // instead of silently consuming another of the 4 version slots.
  const pendingRequestIdRef = useRef(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [showCompareVersions, setShowCompareVersions] = useState(false);

  const [copiedField, setCopiedField] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [factDecisionPending, setFactDecisionPending] = useState(null);

  const [feedbackExpanded, setFeedbackExpanded] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState({ buttonLabel: "Give Feedback + Earn 1 Credit", alreadyDone: false });

  const titleField = useEditableField(result?.userEdits?.title ?? result?.title);
  const mlsField = useEditableField(result?.userEdits?.mlsDescription ?? result?.mlsDescription);
  const captionField = useEditableField(result?.userEdits?.socialCaption ?? result?.socialCaption);
  const extendedField = useEditableField(result?.userEdits?.extendedDescription ?? result?.extendedDescription);

  // Loading step: time-based progress + rotating message, same honest
  // (not-fake-granular) pattern StrategyPricingModal.jsx uses - never claims
  // real backend progress this frontend doesn't actually know.
  useEffect(() => {
    if (step !== "loading") {
      setLoadingProgress(0);
      setLoadingMessageIndex(0);
      return;
    }
    const startedAt = Date.now();
    const CAP = 90;
    const TIME_CONSTANT_MS = 20000;
    setLoadingProgress(2);
    const interval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setLoadingProgress(CAP * (1 - Math.exp(-elapsed / TIME_CONSTANT_MS)));
      setLoadingMessageIndex(Math.min(LOADING_MESSAGES.length - 1, Math.floor(elapsed / 4000)));
    }, 300);
    return () => clearInterval(interval);
  }, [step]);

  function loadVersions() {
    if (!transaction?.id || !result?.propertyKey) return;
    setVersionsLoading(true);
    functionGetStrategyGenerationsForProperty({
      transactionId: transaction.id,
      propertyKey: result.propertyKey,
      strategyType: "listingDescription",
    })
      .then((data) => setVersions(data || []))
      .catch(() => setVersions([]))
      .finally(() => setVersionsLoading(false));
  }

  useEffect(() => {
    if (step === "result" && result?.propertyKey) {
      loadVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, result?.propertyKey, result?.generationId]);

  function addPriorityItem() {
    const text = priorityItemDraft.trim();
    if (!text) return;
    if (priorityItems.length >= 6) {
      toast.info("You can add up to 6 priority items.");
      return;
    }
    setPriorityItems((prev) => [...prev, { id: `${Date.now()}_${prev.length}`, text }]);
    setPriorityItemDraft("");
  }

  function removePriorityItem(id) {
    setPriorityItems((prev) => prev.filter((item) => item.id !== id));
  }

  function showsSellerMotivationNote(items) {
    const combined = items.map((i) => i.text.toLowerCase()).join(" ");
    return SELLER_MOTIVATION_KEYWORDS.some((kw) => combined.includes(kw));
  }

  async function handleGenerate(isRegeneration) {
    if (!transaction?.id) return;
    // Reuse the same requestId only when this click is a retry of the
    // immediately-prior FAILED attempt - a genuinely new initial generation
    // or regeneration click gets a new id.
    const isRetryOfFailedAttempt = Boolean(error) && Boolean(pendingRequestIdRef.current);
    if (!isRetryOfFailedAttempt) {
      pendingRequestIdRef.current = window.crypto.randomUUID();
    }
    setError(null);
    setStep("loading");
    try {
      const pd = transaction?.propertyDetails || {};
      const mlsPropertyDetails = {
        bedroomsTotal: pd.bedroomsTotal ?? null,
        bathroomsFull: pd.bathroomsFull ?? null,
        bathroomsHalf: pd.bathroomsHalf ?? null,
        bathroomsThreeQuarter: pd.bathroomsThreeQuarter ?? null,
        aboveGradeFinishedSqft: pd.aboveGradeFinishedSqft ?? null,
        belowGradeFinishedSqft: pd.belowGradeFinishedSqft ?? null,
        belowGradeUnfinishedSqft: pd.belowGradeUnfinishedSqft ?? null,
        basement: pd.basement || [],
        garageSpaces: pd.garageSpaces ?? null,
        lotSizeSqft: pd.lotSizeSqft ?? null,
        levels: pd.levels || [],
        propertySubType: pd.propertySubType || "",
        view: pd.view || [],
        waterfrontYN: pd.waterfrontYN ?? null,
        associationFee: pd.associationFee ?? null,
        associationFeeIncludes: pd.associationFeeIncludes || [],
        publicRemarks: pd.publicRemarks || "",
      };

      const generated = await functionGenerateListingDescriptionStrategy({
        transactionId: transaction.id,
        requestId: pendingRequestIdRef.current,
        propertyKey: result?.propertyKey || undefined,
        originalDraft: isRegeneration ? undefined : `${originalDraft}${emphasis ? `\n\nEmphasize: ${emphasis}` : ""}`,
        priorityItems: isRegeneration ? undefined : priorityItems.map((i) => ({ id: i.id, text: i.text, channels: ["all"] })),
        otherPropertyDetails: isRegeneration ? undefined : otherPropertyDetails,
        writingStyle,
        regenerationInstruction: isRegeneration ? regenerationInstruction : undefined,
        propertyType: pd.propertyType || transaction?.propertyType || null,
        yearBuilt: pd.yearBuilt || null,
        mlsPropertyDetails,
        // Only matters on the very first generation (the backend never
        // re-researches on a regenerate) - if the Pricing Strategy already
        // researched this same property, reuse those facts instead of
        // making TransActioner research the same address twice.
        pricingPropertyResearch: isRegeneration ? undefined : transaction?.strategy?.propertyResearch || undefined,
      });

      pendingRequestIdRef.current = null;
      const newResult = { ...generated, generatedAt: new Date() };
      setResult(newResult);
      setStep("result");
      setShowRegenerate(false);
      setRegenerationInstruction("");
      titleField.reset();
      mlsField.reset();
      captionField.reset();
      extendedField.reset();
      if (onGenerated) {
        onGenerated({
          generationId: generated.generationId,
          propertyKey: generated.propertyKey,
          generationNumber: generated.generationNumber,
          result: newResult,
        });
      }
    } catch (err) {
      console.error("generateListingDescriptionStrategy failed:", err);
      if (err?.details?.code === "REGENERATION_LIMIT_REACHED") {
        toast.error("All regenerations for this property have already been used. Refresh to see the latest version.");
        setStep("result");
      } else {
        // Never the raw server/API error text - the credit (if any) was
        // spent at the earlier unlock step, not by this attempt, so this is
        // always true regardless of what actually failed.
        const friendlyMessage =
          "We couldn't complete your strategy because the AI service is temporarily unavailable. Your property remains unlocked, and you can retry without using another credit.";
        setError(friendlyMessage);
        toast.error(friendlyMessage);
        setStep(result ? "result" : "form");
      }
    }
  }

  function handleClose() {
    dispatch(closeModal({ modalType: "StrategyListingDescriptionModal" }));
  }

  async function handleCopy(field, text) {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopiedField(field);
      toast.success("Copied");
      setTimeout(() => setCopiedField((f) => (f === field ? null : f)), 2000);
    } catch (err) {
      toast.error("Could not copy to clipboard");
    }
  }

  async function saveEdit(field, value) {
    if (!transaction?.id || !result?.propertyKey || !result?.generationId) return;
    setSavingField(field);
    try {
      await functionUpdateListingDescriptionEdits({
        transactionId: transaction.id,
        propertyKey: result.propertyKey,
        generationId: result.generationId,
        edits: { [field]: value },
      });
    } catch (err) {
      toast.error("Could not save your edit - please try again.");
    } finally {
      setSavingField(null);
    }
  }

  async function handleFactDecision(fact, decision) {
    if (!transaction?.id || !result?.propertyKey) return;
    setFactDecisionPending(fact);
    try {
      await functionUpdateListingDescriptionFactDecision({
        transactionId: transaction.id,
        propertyKey: result.propertyKey,
        generationId: result.generationId,
        factKey: fact,
        decision,
      });
      setResult((prev) => ({
        ...prev,
        _confirmedFacts: decision === "confirmed" ? [...(prev._confirmedFacts || []), fact] : (prev._confirmedFacts || []).filter((f) => f !== fact),
        _rejectedFacts: decision === "rejected" ? [...(prev._rejectedFacts || []), fact] : (prev._rejectedFacts || []).filter((f) => f !== fact),
      }));
      toast.success(decision === "confirmed" ? "Saved - this will be included when you regenerate." : "Saved - this will be excluded.");
    } catch (err) {
      toast.error("Could not save that decision - please try again.");
    } finally {
      setFactDecisionPending(null);
    }
  }

  async function handleUseVersion(version) {
    if (!transaction?.id || !result?.propertyKey) return;
    try {
      await functionSelectListingDescriptionVersion({
        transactionId: transaction.id,
        propertyKey: result.propertyKey,
        generationId: version.id,
      });
      const newResult = {
        ...version.result,
        userEdits: version.userEdits || null,
        generationId: version.id,
        generationNumber: version.generationNumber,
        propertyKey: result.propertyKey,
        generatedAt: version.createdAt,
      };
      setResult(newResult);
      if (onVersionSelected) onVersionSelected({ generationId: version.id, propertyKey: result.propertyKey, result: newResult });
      toast.success(`Using Version ${version.generationNumber}`);
      setShowCompareVersions(false);
    } catch (err) {
      toast.error("Could not switch to that version - please try again.");
    }
  }

  // Shared writing-style card picker (icon + label + description), reused by
  // both the initial form step and the Regenerate section so switching style
  // before a regeneration is exactly as informative as picking it the first
  // time.
  function renderWritingStyleCards(selectedValue, onSelect) {
    return (
      <Grid stackable columns={3}>
        {WRITING_STYLES.map((s) => (
          <Grid.Column key={s.value}>
            <Segment
              onClick={() => onSelect(s.value)}
              style={{
                cursor: "pointer",
                textAlign: "center",
                borderColor: selectedValue === s.value ? "#2185d0" : undefined,
                borderWidth: selectedValue === s.value ? "2px" : "1px",
                background: selectedValue === s.value ? "#eaf4fc" : undefined,
              }}
            >
              <Icon name={s.icon} size="big" color={selectedValue === s.value ? "blue" : "grey"} />
              <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>
                {selectedValue === s.value && <Icon name="check circle" color="blue" />}
                {s.label}
              </div>
              <div className="text-grey" style={{ fontSize: "0.85em" }}>{s.description}</div>
            </Segment>
          </Grid.Column>
        ))}
      </Grid>
    );
  }

  function renderFormStep() {
    return (
      <>
        <Header as="h2" className="zero bottom margin">Create a Better Listing Description</Header>
        <p className="text-grey small top margin">
          Start with a rough draft, bullet points, or a few important features. TransActioner will research the
          property, identify useful details, check for potential Fair Housing concerns, and create polished listing
          descriptions.
        </p>

        <Form style={{ marginTop: "16px" }}>
          <Form.Field>
            <label>Writing Style</label>
            {renderWritingStyleCards(writingStyle, setWritingStyle)}
          </Form.Field>

          <Form.Field>
            <label>Rough Listing Description / Property Highlights</label>
            <Form.TextArea
              rows={6}
              value={originalDraft}
              onChange={(e) => setOriginalDraft(e.target.value)}
              placeholder="Updated kitchen, hardwood floors, large fenced yard, end unit, unfinished basement, mountain views, attached 2-car garage, HOA pool, close to trails..."
            />
          </Form.Field>

          <Form.Field>
            <label>What should we emphasize? <span className="text-grey" style={{ fontWeight: 400 }}>(optional)</span></label>
            <Input
              fluid
              value={emphasis}
              onChange={(e) => setEmphasis(e.target.value)}
              placeholder="Large yard, views and trail access"
            />
          </Form.Field>

          <Form.Field>
            <label>Must Include / Priority Messages <span className="text-grey" style={{ fontWeight: 400 }}>(optional, up to 6)</span></label>
            <p className="text-grey small bottom margin" style={{ fontSize: "0.85em" }}>
              Add the property features, benefits or seller messages you want emphasized.
            </p>
            <Input
              fluid
              placeholder="e.g. Mountain views, Motivated Seller, Kitchen remodeled in 2024"
              value={priorityItemDraft}
              onChange={(e) => setPriorityItemDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addPriorityItem();
                }
              }}
              action={{ icon: "plus", onClick: addPriorityItem, type: "button" }}
            />
            {priorityItems.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                {priorityItems.map((item) => (
                  <Label key={item.id} style={{ marginBottom: "6px" }}>
                    {item.text}
                    <Icon name="delete" onClick={() => removePriorityItem(item.id)} style={{ cursor: "pointer" }} />
                  </Label>
                ))}
              </div>
            )}
            {showsSellerMotivationNote(priorityItems) && (
              <Message size="small" info style={{ marginTop: "10px" }}>
                Seller-motivation language may affect negotiating posture. Confirm that the Seller approves this
                messaging before publishing.
              </Message>
            )}
          </Form.Field>

          <Form.Field>
            <label>Other Property Details <span className="text-grey" style={{ fontWeight: 400 }}>(optional)</span></label>
            <p className="text-grey small bottom margin" style={{ fontSize: "0.85em" }}>
              Add anything else that may be useful. TransActioner will decide which details strengthen the
              description and which can be left out.
            </p>
            <Form.TextArea
              rows={3}
              value={otherPropertyDetails}
              onChange={(e) => setOtherPropertyDetails(e.target.value)}
              placeholder="Hardwood floors, fireplace, pantry, upstairs laundry, patio, garden beds, unfinished storage room, south-facing windows..."
            />
          </Form.Field>
        </Form>

        {error && (
          <Message negative style={{ marginTop: "16px" }}>
            {error}
          </Message>
        )}

        <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button basic onClick={handleClose}>Close</Button>
          <Button primary icon labelPosition="left" onClick={() => handleGenerate(false)}>
            <Icon name="magic" />
            Generate Listing Description
          </Button>
        </div>
      </>
    );
  }

  function renderLoadingStep() {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <Header as="h3">{LOADING_MESSAGES[loadingMessageIndex]}</Header>
        <Progress percent={Math.round(loadingProgress)} indicating style={{ maxWidth: "400px", margin: "20px auto 0" }} />
      </div>
    );
  }

  function renderEditableSection({ label, fieldState, fieldKey, multiline, extra }) {
    return (
      <Segment>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Header as="h4" className="zero bottom margin">{label}</Header>
          <span style={{ fontSize: "0.8em" }}>
            {savingField === fieldKey && <span className="text-grey">Saving…</span>}
            {savingField !== fieldKey && fieldState.edited && <span className="text-grey">Saved</span>}
          </span>
        </div>
        {multiline ? (
          <textarea
            value={fieldState.value}
            onChange={(e) => fieldState.onChange(e.target.value)}
            onBlur={() => fieldState.edited && saveEdit(fieldKey, fieldState.value)}
            rows={fieldKey === "mlsDescription" ? 6 : 10}
            style={{
              width: "100%", fontFamily: "inherit", fontSize: "0.95em", padding: "8px", marginTop: "8px",
              border: "1px solid rgba(34,36,38,0.15)", borderRadius: "4px", resize: "vertical",
            }}
          />
        ) : (
          <Input
            fluid
            value={fieldState.value}
            onChange={(e) => fieldState.onChange(e.target.value)}
            onBlur={() => fieldState.edited && saveEdit(fieldKey, fieldState.value)}
            style={{ marginTop: "8px" }}
          />
        )}
        {extra}
        <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
          <Button size="small" icon labelPosition="left" onClick={() => handleCopy(fieldKey, fieldState.value)}>
            <Icon name="copy outline" />
            {copiedField === fieldKey ? "Copied" : "Copy"}
          </Button>
        </div>
      </Segment>
    );
  }

  function renderReviewGroup(title, items, iconColor, mapItem) {
    if (!items || items.length === 0) return null;
    return (
      <div style={{ marginBottom: "16px" }}>
        <Header as="h5" className="zero bottom margin">{title}</Header>
        {items.map((item, idx) => (
          <Segment key={idx} secondary style={{ marginTop: "8px" }}>
            {mapItem(item, iconColor)}
          </Segment>
        ))}
      </div>
    );
  }

  function renderResultStep() {
    const confirmedFacts = new Set(result?._confirmedFacts || []);
    const rejectedFacts = new Set(result?._rejectedFacts || []);
    const remainingRegenerations = result?.regenerationsRemaining ?? Math.max(0, 3 - ((result?.generationNumber || 1) - 1));
    const selectedGenerationId = result?.generationId;
    const latestVersion = versions[versions.length - 1];
    const isViewingLatest = !latestVersion || latestVersion.id === selectedGenerationId;

    const fullAddress = convertAddressFull(transaction?.address);

    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Header as="h2" className="zero bottom margin">Listing Description Strategy</Header>
            {fullAddress && (
              <div className="text-grey" style={{ fontSize: "1em", marginTop: "4px" }}>
                {fullAddress}
              </div>
            )}
            {toDisplayDateTime(result?.generatedAt) && (
              <div className="text-grey" style={{ fontSize: "0.85em", marginTop: "2px" }}>
                Generated {toDisplayDateTime(result.generatedAt)}
              </div>
            )}
          </div>
          <Button basic icon labelPosition="left" size="small" className="no-print" onClick={() => window.print()}>
            <Icon name="print" />
            Print
          </Button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}>
          <Label color="blue" basic>
            Style: {WRITING_STYLES.find((s) => s.value === (result?.writingStyle || "professional"))?.label}
          </Label>
          {!isViewingLatest && (
            <Label color="grey" basic>Viewing Version {result?.generationNumber} — not the most recent</Label>
          )}
        </div>

        {result?.finalComplianceFlag && (
          <Message warning style={{ marginTop: "12px" }}>
            <Icon name="warning sign" />
            This version may still contain wording that needs review before publishing. Check the Fair Housing &
            Advertising Review section below carefully.
          </Message>
        )}

        {result?.researchMetadata?.available === false && (
          <Message info style={{ marginTop: "12px" }}>
            No previous listing information was found. Your descriptions were created using the property
            information and draft you provided.
          </Message>
        )}

        <Grid stackable style={{ marginTop: "8px" }}>
          <Grid.Column width={16}>
            {renderEditableSection({ label: "Listing Title", fieldState: titleField, fieldKey: "title" })}

            {renderEditableSection({
              label: "MLS Description", fieldState: mlsField, fieldKey: "mlsDescription", multiline: true,
              extra: (
                <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span
                    className={mlsField.value.length > (result?.mlsCharacterLimit || 1000) ? "text red" : "text-grey"}
                    style={{ fontSize: "0.85em" }}
                  >
                    {mlsField.value.length} / {result?.mlsCharacterLimit || 1000} characters
                  </span>
                </div>
              ),
            })}

            {renderEditableSection({
              label: "Social Media Caption", fieldState: captionField, fieldKey: "socialCaption", multiline: true,
              extra: result?.socialHashtags?.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {result.socialHashtags.map((h, idx) => (
                    <Label key={idx} basic size="small" style={{ marginBottom: "4px" }}>{h}</Label>
                  ))}
                  <div style={{ marginTop: "8px" }}>
                    <Button size="small" basic onClick={() => handleCopy("captionWithHashtags", `${captionField.value}\n\n${result.socialHashtags.join(" ")}`)}>
                      {copiedField === "captionWithHashtags" ? "Copied" : "Copy Caption + Hashtags"}
                    </Button>
                  </div>
                </div>
              ),
            })}

            {renderEditableSection({ label: "Extended Description", fieldState: extendedField, fieldKey: "extendedDescription", multiline: true })}
          </Grid.Column>
        </Grid>

        {result?.priorityItemCoverage?.length > 0 && (
          <Segment style={{ marginTop: "12px" }}>
            <Header as="h4">Priority Messages</Header>
            <List>
              {result.priorityItemCoverage.map((coverage, idx) => {
                const meta = PRIORITY_ITEM_STATUS_META[coverage.status] || PRIORITY_ITEM_STATUS_META.included;
                return (
                  <List.Item key={idx}>
                    <Icon name={meta.icon} style={{ color: meta.color }} />
                    <List.Content>
                      <b>{coverage.item}</b>
                      {coverage.reason && <span className="text-grey"> — {coverage.reason}</span>}
                    </List.Content>
                  </List.Item>
                );
              })}
            </List>
          </Segment>
        )}

        {result?.propertyDifferentiators?.length > 0 && (
          <Segment style={{ marginTop: "12px" }}>
            <Header as="h4">What Makes This Home Stand Out</Header>
            <List>
              {result.propertyDifferentiators.map((d, idx) => (
                <List.Item key={idx}>
                  <Icon name="check" color="green" />
                  <List.Content>{d.fact}</List.Content>
                </List.Item>
              ))}
            </List>
          </Segment>
        )}

        <Segment style={{ marginTop: "12px" }}>
          <Header as="h4">Fair Housing & Advertising Review</Header>
          {(!result?.fairHousingReview?.items || result.fairHousingReview.items.length === 0) &&
            (!result?.accuracyAdvertisingReview || result.accuracyAdvertisingReview.length === 0) && (
              <Message positive size="small">
                <Icon name="check circle" />
                No potential Fair Housing advertising concerns detected in your draft.
              </Message>
            )}

          {renderReviewGroup("Fair Housing Concerns", result?.fairHousingReview?.items, null, (item) => (
            <>
              <div style={{ fontWeight: 700 }}><Icon name="warning sign" color="yellow" /> "{item.originalText}"</div>
              <p style={{ marginTop: "6px" }}>{item.concern}</p>
              {item.suggestedAlternative && (
                <>
                  <div className="text-grey" style={{ fontSize: "0.85em", fontWeight: 700, marginTop: "8px" }}>A BETTER WAY TO DESCRIBE THIS:</div>
                  <p style={{ fontStyle: "italic" }}>"{item.suggestedAlternative}"</p>
                </>
              )}
            </>
          ))}

          {renderReviewGroup("Accuracy & Advertising", result?.accuracyAdvertisingReview, null, (item) => (
            <>
              <div style={{ fontWeight: 700 }}><Icon name="warning sign" color="orange" /> "{item.originalText}"</div>
              <p style={{ marginTop: "6px" }}>{item.concern}</p>
              {item.suggestedAlternative && (
                <>
                  <div className="text-grey" style={{ fontSize: "0.85em", fontWeight: 700, marginTop: "8px" }}>A BETTER WAY TO DESCRIBE THIS:</div>
                  <p style={{ fontStyle: "italic" }}>"{item.suggestedAlternative}"</p>
                </>
              )}
            </>
          ))}

          {renderReviewGroup("Style Suggestions", result?.styleWarnings, null, (item) => (
            <>
              <div style={{ fontWeight: 700 }}>"{item.originalText}"</div>
              <p style={{ marginTop: "6px" }} className="text-grey">{item.reason}</p>
              {item.suggestedAlternative && <p style={{ fontStyle: "italic" }}>Consider: "{item.suggestedAlternative}"</p>}
            </>
          ))}

          {renderReviewGroup("Positive Conversions", result?.positiveConversions, null, (item) => (
            <>
              <div style={{ fontWeight: 700 }}><Icon name="check circle" color="green" /> "{item.originalText}" → "{item.replacementText}"</div>
              <p style={{ marginTop: "6px" }} className="text-grey">{item.reason}</p>
            </>
          ))}

          <Message size="small" style={{ marginTop: "10px" }}>
            AI-assisted advertising review is provided as a drafting aid and is not a substitute for broker, MLS,
            legal, or Fair Housing compliance review.
          </Message>
        </Segment>

        {result?.detailsAdded?.length > 0 && (
          <Segment style={{ marginTop: "12px" }}>
            <Header as="h4">Details Added From Research</Header>
            <List>
              {result.detailsAdded.map((d, idx) => (
                <List.Item key={idx}>
                  <Icon name="check" color="green" />
                  <List.Content>
                    <b>{d.fact}</b>
                    {d.sourceType && <div className="text-grey" style={{ fontSize: "0.85em" }}>Source: {d.sourceType}{d.sourceDescription ? ` — ${d.sourceDescription}` : ""}</div>}
                  </List.Content>
                </List.Item>
              ))}
            </List>
          </Segment>
        )}

        {result?.verifyBeforePublishing?.length > 0 && (
          <Segment style={{ marginTop: "12px" }}>
            <Header as="h4">Verify Before Publishing</Header>
            <List divided relaxed>
              {result.verifyBeforePublishing.map((item, idx) => {
                const isConfirmed = confirmedFacts.has(item.fact);
                const isRejected = rejectedFacts.has(item.fact);
                return (
                  <List.Item key={idx}>
                    <Icon name="warning sign" color="orange" />
                    <List.Content>
                      <b>{item.fact}</b>
                      <p className="text-grey" style={{ marginTop: "4px" }}>{item.reason}</p>
                      <div style={{ marginTop: "8px" }}>
                        <Button
                          size="mini"
                          positive={isConfirmed}
                          loading={factDecisionPending === item.fact}
                          onClick={() => handleFactDecision(item.fact, "confirmed")}
                        >
                          {isConfirmed ? "Confirmed ✓" : "Confirm Fact"}
                        </Button>
                        <Button
                          size="mini"
                          basic
                          negative={isRejected}
                          loading={factDecisionPending === item.fact}
                          onClick={() => handleFactDecision(item.fact, "rejected")}
                        >
                          Don't Include
                        </Button>
                      </div>
                      {isConfirmed && (
                        <p className="text-grey" style={{ fontSize: "0.85em", marginTop: "4px" }}>
                          Confirmed facts will be available for edits and future regenerations.
                        </p>
                      )}
                    </List.Content>
                  </List.Item>
                );
              })}
            </List>
          </Segment>
        )}

        <Segment style={{ marginTop: "12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Header as="h4" className="zero bottom margin">Regenerate</Header>
            <Popup
              disabled={remainingRegenerations > 0}
              content="All 3 regenerations have already been used for this property. You can still edit any version below."
              trigger={
                <span>
                  <Button
                    basic
                    size="small"
                    disabled={remainingRegenerations <= 0}
                    onClick={() => setShowRegenerate((v) => !v)}
                  >
                    {showRegenerate ? "Cancel" : "Regenerate"}
                  </Button>
                </span>
              }
            />
          </div>
          {remainingRegenerations > 0 ? (
            <p className="text-grey small top margin">{remainingRegenerations} regeneration{remainingRegenerations === 1 ? "" : "s"} remaining</p>
          ) : (
            <p className="small top margin">
              You've used all 3 regenerations for this property. You can continue editing any version below.
            </p>
          )}
          {showRegenerate && remainingRegenerations > 0 && (
            <Form style={{ marginTop: "12px" }}>
              <Form.Field>
                <label>Writing Style</label>
                {renderWritingStyleCards(writingStyle, setWritingStyle)}
              </Form.Field>
              <Form.Field>
                <label>What would you like to change? <span className="text-grey" style={{ fontWeight: 400 }}>(optional)</span></label>
                <textarea
                  rows={3}
                  value={regenerationInstruction}
                  onChange={(e) => setRegenerationInstruction(e.target.value)}
                  placeholder="Make it less salesy and emphasize the backyard and trail access."
                  style={{
                    width: "100%", fontFamily: "inherit", fontSize: "0.95em", padding: "8px",
                    border: "1px solid rgba(34,36,38,0.15)", borderRadius: "4px", resize: "vertical",
                  }}
                />
              </Form.Field>
              <Button primary icon labelPosition="left" onClick={() => handleGenerate(true)}>
                <Icon name="redo" />
                Regenerate
              </Button>
            </Form>
          )}
        </Segment>

        <Segment style={{ marginTop: "12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }} onClick={() => setShowVersionHistory((v) => !v)}>
              <Header as="h4" className="zero bottom margin">Version History</Header>
              <Icon name={showVersionHistory ? "chevron up" : "chevron down"} />
            </div>
            {versions.length > 1 && (
              <Button size="small" basic icon labelPosition="left" onClick={() => setShowCompareVersions(true)}>
                <Icon name="columns" />
                Compare All Versions
              </Button>
            )}
          </div>
          {showVersionHistory && (
            <List divided relaxed style={{ marginTop: "12px" }}>
              {versionsLoading && <p className="text-grey">Loading versions...</p>}
              {!versionsLoading && [...versions].reverse().map((version) => {
                const isSelected = version.id === selectedGenerationId;
                const isLatest = latestVersion && version.id === latestVersion.id;
                return (
                  <List.Item key={version.id}>
                    <List.Content floated="right">
                      {isSelected ? (
                        <Label size="tiny" color="blue">Selected Version</Label>
                      ) : (
                        <Button size="mini" basic onClick={() => handleUseVersion(version)}>Use This Version</Button>
                      )}
                    </List.Content>
                    <List.Content>
                      <b>Version {version.generationNumber}{isLatest ? " – Most Recent" : ""}</b>
                      <div className="text-grey" style={{ fontSize: "0.85em" }}>
                        Generated {toDisplayDateTime(version.createdAt)}
                        {version.writingStyle && ` · ${WRITING_STYLES.find((s) => s.value === version.writingStyle)?.label || version.writingStyle}`}
                      </div>
                      {version.regenerationInstruction && (
                        <div className="text-grey" style={{ fontSize: "0.85em" }}>Instruction: "{version.regenerationInstruction}"</div>
                      )}
                    </List.Content>
                  </List.Item>
                );
              })}
            </List>
          )}
        </Segment>

        <Divider />
        <StrategyFeedback
          transactionId={transaction?.id}
          propertyKey={result?.propertyKey}
          propertyAddress={transaction?.address}
          strategyType="listingDescription"
          generationId={result?.generationId}
          generatedRecommendationSnapshot={{ title: result?.title }}
          engineMetadata={{ modelName: result?.modelName || null }}
          config={buildListingDescriptionFeedbackConfig()}
          expanded={feedbackExpanded}
          onToggleExpanded={setFeedbackExpanded}
          onStatusChange={setFeedbackStatus}
          hideToggle
        />

        <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
          {!feedbackExpanded && !feedbackStatus.hidden && (
            <Button basic disabled={feedbackStatus.alreadyDone} onClick={() => setFeedbackExpanded(true)}>
              {feedbackStatus.buttonLabel}
            </Button>
          )}
          <Button onClick={handleClose}>Close</Button>
        </div>
      </>
    );
  }

  // All generated versions side-by-side for direct comparison, rather than
  // opening them one at a time from Version History. Horizontally
  // scrollable (fixed-width cards) instead of a stacking Grid, so up to 4
  // versions genuinely line up next to each other even on a narrower
  // screen - stacking them would defeat the point of a side-by-side view.
  function renderCompareVersionsView() {
    const orderedVersions = [...versions].reverse();
    const selectedGenerationId = result?.generationId;
    const latestVersion = versions[versions.length - 1];
    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <Header as="h2" className="zero bottom margin">Compare All Versions</Header>
          <Button basic icon labelPosition="left" onClick={() => setShowCompareVersions(false)}>
            <Icon name="close" />
            Back to Editor
          </Button>
        </div>
        <p className="text-grey small top margin">
          Every generated version for this property, side-by-side. Selecting a version here sets it as the version
          you're viewing and editing - it never deletes any other version.
        </p>

        {/* CSS Grid, one column per version, one ROW per field - not a row of
            independent cards - so the MLS Description row (say) lines up at
            the same vertical position across every version regardless of
            how many lines the title or the regeneration instruction above
            it happen to wrap to. A flex row of self-contained cards can't
            guarantee that: each card's own internal height varies with its
            own content. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${orderedVersions.length}, 380px)`,
            gridAutoFlow: "row",
            columnGap: "24px",
            rowGap: "28px",
            overflowX: "auto",
            paddingBottom: "12px",
            marginTop: "16px",
            alignItems: "start",
            fontSize: "1.05em",
          }}
        >
          {orderedVersions.map((version) => {
            const isSelected = version.id === selectedGenerationId;
            const isLatest = latestVersion && version.id === latestVersion.id;
            const styleLabel = WRITING_STYLES.find((s) => s.value === version.writingStyle);
            return (
              <div
                key={`meta-${version.id}`}
                style={{
                  padding: "14px",
                  border: `1px solid ${isSelected ? "#2185d0" : "rgba(34,36,38,0.15)"}`,
                  borderWidth: isSelected ? "2px" : "1px",
                  borderRadius: "4px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <b style={{ fontSize: "1.05em" }}>Version {version.generationNumber}</b>
                    {isLatest && <div className="text-grey" style={{ fontSize: "0.9em" }}>Most Recent</div>}
                  </div>
                  {isSelected && <Label size="small" color="blue">Selected</Label>}
                </div>
                <div className="text-grey" style={{ fontSize: "0.9em", marginTop: "4px" }}>
                  {toDisplayDateTime(version.createdAt)}
                </div>
                {styleLabel && (
                  <div style={{ marginTop: "6px" }}>
                    <Icon name={styleLabel.icon} />
                    <span style={{ fontSize: "0.95em" }}>{styleLabel.label}</span>
                  </div>
                )}
                {version.regenerationInstruction && (
                  <div className="text-grey" style={{ fontSize: "0.9em", marginTop: "6px", fontStyle: "italic" }}>
                    "{version.regenerationInstruction}"
                  </div>
                )}
              </div>
            );
          })}

          {orderedVersions.map((version) => (
            <div
              key={`title-${version.id}`}
              style={{ padding: "14px", border: "1px solid rgba(34,36,38,0.1)", borderRadius: "4px", fontWeight: 700, fontSize: "1.05em" }}
            >
              {version.userEdits?.title ?? version.result?.title}
            </div>
          ))}

          {orderedVersions.map((version) => (
            <div key={`mls-${version.id}`} style={{ padding: "14px", border: "1px solid rgba(34,36,38,0.1)", borderRadius: "4px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="text-grey" style={{ fontSize: "0.9em", fontWeight: 700 }}>MLS DESCRIPTION</span>
                <Button
                  size="mini"
                  icon
                  basic
                  onClick={() => handleCopy(`compare-${version.id}-mls`, version.userEdits?.mlsDescription ?? version.result?.mlsDescription)}
                >
                  <Icon name="copy outline" />
                  {copiedField === `compare-${version.id}-mls` ? "Copied" : "Copy"}
                </Button>
              </div>
              <div style={{ fontSize: "1em", maxHeight: "320px", overflowY: "auto", whiteSpace: "pre-wrap", marginTop: "8px" }}>
                {version.userEdits?.mlsDescription ?? version.result?.mlsDescription}
              </div>
            </div>
          ))}

          {orderedVersions.map((version) => (
            <div key={`social-${version.id}`} style={{ padding: "14px", border: "1px solid rgba(34,36,38,0.1)", borderRadius: "4px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="text-grey" style={{ fontSize: "0.9em", fontWeight: 700 }}>SOCIAL CAPTION</span>
                <Button
                  size="mini"
                  icon
                  basic
                  onClick={() => handleCopy(`compare-${version.id}-social`, version.userEdits?.socialCaption ?? version.result?.socialCaption)}
                >
                  <Icon name="copy outline" />
                  {copiedField === `compare-${version.id}-social` ? "Copied" : "Copy"}
                </Button>
              </div>
              <div style={{ fontSize: "1em", maxHeight: "130px", overflowY: "auto", whiteSpace: "pre-wrap", marginTop: "8px" }}>
                {version.userEdits?.socialCaption ?? version.result?.socialCaption}
              </div>
            </div>
          ))}

          {orderedVersions.map((version) => (
            <div key={`extended-${version.id}`} style={{ padding: "14px", border: "1px solid rgba(34,36,38,0.1)", borderRadius: "4px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="text-grey" style={{ fontSize: "0.9em", fontWeight: 700 }}>EXTENDED DESCRIPTION</span>
                <Button
                  size="mini"
                  icon
                  basic
                  onClick={() => handleCopy(`compare-${version.id}-extended`, version.userEdits?.extendedDescription ?? version.result?.extendedDescription)}
                >
                  <Icon name="copy outline" />
                  {copiedField === `compare-${version.id}-extended` ? "Copied" : "Copy"}
                </Button>
              </div>
              <div style={{ fontSize: "1em", maxHeight: "220px", overflowY: "auto", whiteSpace: "pre-wrap", marginTop: "8px" }}>
                {version.userEdits?.extendedDescription ?? version.result?.extendedDescription}
              </div>
            </div>
          ))}

          {orderedVersions.map((version) => {
            const isSelected = version.id === selectedGenerationId;
            return (
              <div key={`action-${version.id}`}>
                {isSelected ? (
                  <Button fluid disabled>Currently Selected</Button>
                ) : (
                  <Button fluid primary onClick={() => handleUseVersion(version)}>Use This Version</Button>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <ModalWrapper size={showCompareVersions ? "fullscreen" : "large"}>
      <Segment clearing id="listing-description-strategy-print-area">
        {step === "form" && renderFormStep()}
        {step === "loading" && renderLoadingStep()}
        {step === "result" && (showCompareVersions ? renderCompareVersionsView() : renderResultStep())}
      </Segment>
    </ModalWrapper>
  );
}
